import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, Check, Loader2, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useLang } from '../i18n';
import './AuthScreen.css';
import './TermsAcceptanceGate.css';

const FALLBACK_TERMS_CONTENT = {
  user_terms: {
    title: 'Termos de Uso',
    content: `
# Termos de Uso

Ao usar o Game Pro Hub, você concorda com o uso responsável da plataforma para análises e treinamento.
`
  },
  privacy_policy: {
    title: 'Política de Privacidade',
    content: `
# Política de Privacidade

Dados de perfil, progresso e análises são usados apenas para funcionamento do produto e melhoria da experiência.
`
  }
};

export default function TermsAcceptanceGate({ onAccepted, termsContent }) {
  const { t } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [activeDoc, setActiveDoc] = useState('user_terms');
  const scrollRef = useRef(null);

  const terms = termsContent || FALLBACK_TERMS_CONTENT;
  const loading = false;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    // Increased threshold for better mobile reliability and sub-pixel rounding issues
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    if (nearBottom) setScrolledToBottom(true);
  };

  useEffect(() => {
    // Reset state for new document
    setScrolledToBottom(false);
    
    // Check if content is even scrollable
    const checkScrollable = () => {
      const el = scrollRef.current;
      if (el) {
        // If content height is less than or equal to container, mark as read
        const isScrollable = el.scrollHeight > el.clientHeight + 10;
        if (!isScrollable) {
          setScrolledToBottom(true);
        }
        el.scrollTop = 0;
      }
    };

    // Small delay to ensure ReactMarkdown has rendered
    const timer = setTimeout(checkScrollable, 100);
    return () => clearTimeout(timer);
  }, [activeDoc, terms]);

  const handleAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onAccepted?.({
        types: ['user_terms', 'privacy_policy']
      });
    } catch (err) {
      setError(err.message || t('terms_accept_error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-main)] flex flex-col items-center justify-center p-6 space-y-6">
        <div className="relative w-24 h-24 flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-0 border-2 border-[var(--border-primary)] border-t-[var(--accent-secondary)] rounded-[3px]"
          />
          <Shield size={32} className="text-[var(--accent-secondary)]" />
        </div>
        <p className="text-[10px] text-[var(--text-muted)] font-black uppercase tracking-[0.24em] animate-pulse">
          {t('terms_syncing')}
        </p>
      </div>
    );
  }

  const currentDoc = terms?.[activeDoc];

  return (
    <div className="mi-auth-premium min-h-screen w-full flex items-center justify-center p-4 relative overflow-y-auto">
      {/* Premium Floating Particles background */}
      <div className="mi-auth-particles">
        <div className="mi-auth-particle" style={{ left: '10%', width: '8px', height: '8px', animationDuration: '6s', animationDelay: '0s' }} />
        <div className="mi-auth-particle" style={{ left: '30%', width: '12px', height: '12px', animationDuration: '8s', animationDelay: '2s' }} />
        <div className="mi-auth-particle" style={{ left: '70%', width: '6px', height: '6px', animationDuration: '5s', animationDelay: '1s' }} />
        <div className="mi-auth-particle" style={{ left: '95%', width: '10px', height: '10px', animationDuration: '7s', animationDelay: '3s' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="mi-auth-premium__form-card w-full max-w-[520px] p-6 sm:p-10 flex flex-col gap-6 relative z-10 terms-gate__card"
      >
        <div className="terms-gate__header">
          <div className="terms-gate__shield">
            <Shield size={22} />
          </div>
          <h1>{t('terms_title')}</h1>
          <p>{t('terms_subtitle')}</p>
        </div>

        <div className="terms-gate__tabs">
          {[
            { id: 'user_terms', label: t('terms_user_terms') },
            { id: 'privacy_policy', label: t('terms_privacy') }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveDoc(tab.id)}
              className={`terms-gate__tab ${activeDoc === tab.id ? 'is-active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="terms-gate__doc-wrap">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="terms-gate__doc"
          >
            {currentDoc ? (
              <div className="terms-gate__markdown">
                <ReactMarkdown>{currentDoc.content}</ReactMarkdown>
              </div>
            ) : (
              <p className="terms-gate__empty">{t('terms_not_loaded')}</p>
            )}
          </div>

          {!scrolledToBottom && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="terms-gate__scroll-tip">
              <div className="terms-gate__scroll-pill">
                <ChevronDown size={12} />
                <span>{t('terms_scroll_bottom')}</span>
              </div>
            </motion.div>
          )}
        </div>

        <div className="terms-gate__actions">
          <label
            className={`terms-gate__agree ${agreed ? 'is-agreed' : ''} ${!scrolledToBottom ? 'is-disabled' : ''}`}
          >
            <div className="terms-gate__checkbox-wrap">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => scrolledToBottom && setAgreed(e.target.checked)}
                disabled={!scrolledToBottom}
                className="terms-gate__checkbox"
              />
              {agreed && <Check size={11} className="terms-gate__checkbox-icon" />}
            </div>
            <span className="terms-gate__agree-text">
              {t('terms_acceptance_text')}
            </span>
          </label>

          {error && <p className="terms-gate__error">{error}</p>}

          <button
            onClick={handleAccept}
            disabled={!agreed || submitting}
            className="mi-auth-premium__btn-primary terms-gate__submit uppercase tracking-widest font-black text-xs sm:text-sm transition-all"
          >
            {submitting ? (
              <span className="spinner-wrap">
                <Loader2 size={16} className="animate-spin text-black" />
                <span>Enviando...</span>
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <span className="terms-gate__submit-text">{t('terms_accept_continue')}</span>
                <CheckCircle size={16} />
              </span>
            )}
          </button>
        </div>

        <p className="terms-gate__footer">
          GAME PRO HUB // COMPLIANCE 2026
        </p>
      </motion.div>
    </div>
  );
}

